import { gql, useQuery } from '@apollo/client';

import { buildTransactionStatsFilters, type TransactionStatsScope } from '../../utils/spending/transactionStats';

import type {
  GetTransactionStatsQuery,
  GetTransactionStatsQueryVariables,
} from './__generated__/useTransactionStats.gql';

const GET_TRANSACTION_STATS = gql`
  query GetTransactionStats($filters: TransactionFilters) {
    getTransactionStats(filters: $filters) {
      count
      currencyCode
      min
      max
      average
      total
    }
  }
`;

export type TransactionStats = NonNullable<GetTransactionStatsQuery['getTransactionStats']>;

/**
 * All-time spending numbers for a record page.
 *
 * @param scope The merchant or recurring stream to total; the stream's type picks the sign.
 *   `null` skips the read — a record with no merchant has nothing to total.
 * @returns `stats` is the aggregate once loaded, or null while `loading`, on `error`,
 *   when skipped, when nothing matches, and when the set spans more than one currency.
 */
export const useTransactionStats = (scope: TransactionStatsScope | null) => {
  const { data, loading, error } = useQuery<GetTransactionStatsQuery, GetTransactionStatsQueryVariables>(
    GET_TRANSACTION_STATS,
    { variables: scope ? { filters: buildTransactionStatsFilters(scope) } : undefined, skip: scope === null }
  );

  return { stats: data?.getTransactionStats ?? null, loading, error };
};
